import {Check, X, Minus} from 'lucide-react';
import {MS_PER_DAY} from '@common/constants/time';
import {AssigneeAvatar} from './AssigneeAvatar';

type StepStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED' | 'SKIPPED';

interface StepAssignee {
  id: string;
  name: string;
}

interface OnboardingStep {
  id: string;
  stepKey: string;
  label: string;
  phaseGroup: string;
  order: number;
  isRequired: boolean;
  status: StepStatus;
  slaTargetDays: number;
  assignee: StepAssignee | null;
  completedBy: StepAssignee | null;
  completedAt: string | Date | null;
  startedAt: string | Date | null;
  notes: string | null;
}

interface ChecklistStepRowProps {
  step: OnboardingStep;
  onClick: (stepId: string) => void;
}

function computeElapsedDays(startDate: string | Date): number {
  const start = new Date(startDate).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((now - start) / MS_PER_DAY));
}

function computeDaysAgo(date: string | Date): number {
  const then = new Date(date).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((now - then) / MS_PER_DAY));
}

function StatusCircle({status}: {status: StepStatus}) {
  switch (status) {
    case 'NOT_STARTED':
      return (
        <div className='w-5 h-5 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center' />
      );
    case 'IN_PROGRESS':
      return (
        <div className='w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-50 flex items-center justify-center'>
          <div className='w-2 h-2 rounded-full bg-blue-500' />
        </div>
      );
    case 'COMPLETED':
      return (
        <div className='w-5 h-5 rounded-full border-2 border-emerald-500 bg-emerald-500 flex items-center justify-center'>
          <Check size={12} className='text-white' />
        </div>
      );
    case 'BLOCKED':
      return (
        <div className='w-5 h-5 rounded-full border-2 border-red-500 bg-red-50 flex items-center justify-center'>
          <X size={12} className='text-red-500' />
        </div>
      );
    case 'SKIPPED':
      return (
        <div className='w-5 h-5 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center'>
          <Minus size={12} className='text-gray-400' />
        </div>
      );
  }
}

function SlaBar({
  startedAt,
  slaTargetDays,
}: {
  startedAt: string | Date | null;
  slaTargetDays: number;
}) {
  if (!startedAt) {
    return <div className='h-1 w-24 rounded-full bg-gray-200' />;
  }

  const elapsed = computeElapsedDays(startedAt);
  const ratio = slaTargetDays > 0 ? elapsed / slaTargetDays : 0;
  const widthPercent = Math.min(100, ratio * 100);

  let fillColor = 'bg-emerald-500';
  let glowClass = '';
  if (ratio > 1) {
    fillColor = 'bg-red-500';
    glowClass = 'ring-2 ring-red-200';
  } else if (ratio > 0.8) {
    fillColor = 'bg-red-500';
  } else if (ratio > 0.5) {
    fillColor = 'bg-amber-500';
  }

  return (
    <div className={`h-1 w-24 rounded-full bg-gray-200 ${glowClass}`}>
      <div className={`h-full rounded-full ${fillColor}`} style={{width: `${widthPercent}%`}} />
    </div>
  );
}

function TimeLabel({step}: {step: OnboardingStep}) {
  if (step.status === 'COMPLETED' && step.completedAt) {
    const days = computeDaysAgo(step.completedAt);
    return <span className='text-xs text-gray-500'>Completed {days}d ago</span>;
  }

  if (step.startedAt && (step.status === 'IN_PROGRESS' || step.status === 'BLOCKED')) {
    const elapsed = computeElapsedDays(step.startedAt);
    return (
      <span className='text-xs text-gray-500'>
        {elapsed}d / {step.slaTargetDays}d
      </span>
    );
  }

  return <span className='text-xs text-gray-500'>{step.slaTargetDays}d SLA</span>;
}

export function ChecklistStepRow({step, onClick}: ChecklistStepRowProps) {
  const isStrikethrough = step.status === 'SKIPPED' || step.status === 'COMPLETED';

  return (
    <div
      className='flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-all'
      onClick={() => onClick(step.id)}
    >
      <StatusCircle status={step.status} />

      <div className='flex-1 min-w-0'>
        <p
          className={`text-sm font-medium ${isStrikethrough ? 'line-through text-gray-400' : 'text-gray-900'}`}
        >
          {step.label}
        </p>
        <div className='flex items-center gap-2 mt-1'>
          <SlaBar startedAt={step.startedAt} slaTargetDays={step.slaTargetDays} />
          <TimeLabel step={step} />
        </div>
      </div>

      <div className='shrink-0'>
        {step.assignee ? (
          <AssigneeAvatar name={step.assignee.name} size='sm' />
        ) : step.isRequired ? (
          <span className='text-[10px] text-gray-400'>Unassigned</span>
        ) : null}
      </div>
    </div>
  );
}

export type {OnboardingStep, StepStatus};
